import React, { useState, useRef, useEffect } from 'react';
import { 
  Fab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  TextField, 
  IconButton, 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  Chat as ChatIcon, 
  Send as SendIcon, 
  Close as CloseIcon,
  SmartToy as BotIcon,
  OpenInFull as MaximizeIcon,
  CloseFullscreen as MinimizeIcon
} from '@mui/icons-material';
import './ChatBot.scss';

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! Soy el asistente virtual de Sentry. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Contexto del sistema basado en el README
  const systemContext = `Eres un asistente virtual especializado en el sistema Sentry, una plataforma completa de gestión de seguridad que incluye:

**Características Principales del Sistema:**
- Panel Web Administrativo para gestión de clientes, servicios y personal
- Aplicación móvil para vigilantes con formularios dinámicos
- Sistema de cuadrantes y turnos
- Control de rondas con verificación NFC y GPS
- Gestión de documentos y formularios personalizados
- Sistema de notificaciones internas y push
- Buzón anónimo para denuncias
- Reportes y analytics avanzados

**Funcionalidades Específicas:**
1. **Gestión de Usuarios**: CRUD de vigilantes, asignación de servicios, control de roles
2. **Clientes y Servicios**: Registro de clientes, gestión de tiendas, asignación de personal
3. **Formularios Dinámicos**: Constructor visual con campos de texto, números, fechas, archivos, firmas, dropdowns
4. **Cuadrantes**: Gestión por ubicación geográfica, asignación automática de personal
5. **Control de Rondas**: Creación de rutas, verificación NFC/GPS, tracking en tiempo real
6. **Notificaciones**: Push en tiempo real, filtrado por cliente/servicio
7. **Documentos**: Almacenamiento seguro, categorización, búsqueda avanzada
8. **Reportes**: Dashboard interactivo, gráficos, exportación PDF/Excel

**Tecnologías:**
- Frontend: React.js, Material-UI, Firebase
- Backend: Firebase (Firestore, Auth, Storage, Cloud Functions)
- Móvil: React Native, Expo

**Instrucciones:**
- Responde de manera amigable y profesional
- Proporciona información específica sobre las funcionalidades
- Si no sabes algo específico, sugiere consultar la documentación
- Mantén las respuestas concisas pero informativas
- Usa un tono de soporte técnico pero accesible`;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [open]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Debug: Verificar si la API key está disponible
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    console.log('API Key disponible:', apiKey ? 'Sí' : 'No');
    console.log('API Key length:', apiKey ? apiKey.length : 0);
    
    if (!apiKey) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Error: No se encontró la API key de OpenAI. Por favor, verifica que la variable de entorno REACT_APP_OPENAI_API_KEY esté configurada correctamente.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      // Construir el historial de conversación
      const conversationHistory = [
        { role: 'system', content: systemContext },
        ...messages.slice(-10).map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        })),
        { role: 'user', content: userMessage.text }
      ];

      console.log('Enviando petición a OpenAI...');
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: conversationHistory,
          max_tokens: 500,
          temperature: 0.7,
        })
      });

      console.log('Respuesta recibida:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Error de OpenAI:', errorData);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Datos recibidos:', data);
      
      const botMessage = {
        id: Date.now() + 1,
        text: data.choices[0].message.content,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error completo:', error);
      
      let errorText = "Lo siento, estoy teniendo problemas para procesar tu mensaje. ";
      
      if (error.message.includes('401')) {
        errorText += "Error de autenticación: Verifica que la API key sea válida.";
      } else if (error.message.includes('429')) {
        errorText += "Límite de peticiones excedido. Intenta de nuevo en unos minutos.";
      } else if (error.message.includes('500')) {
        errorText += "Error del servidor de OpenAI. Intenta de nuevo.";
      } else {
        errorText += `Error: ${error.message}`;
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const handleClose = () => {
    setOpen(false);
    setIsMaximized(false); // Reset al cerrar
  };

  return (
    <>
      {/* Botón flotante con margen aumentado */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setOpen(true)}
        size="small"
        sx={{
          position: 'fixed',
          bottom: 16, // Pegado más abajo
          right: 16,  // Pegado más a la derecha
          zIndex: 1000,
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
          transition: 'all 0.3s ease'
        }}
      >
        <ChatIcon sx={{ fontSize: 20 }} />
      </Fab>

      {/* Dialog del chat */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isMaximized ? "sm" : false}
        fullWidth={isMaximized}
        PaperProps={{
          sx: {
            height: isMaximized ? '70vh' : '400px',
            maxHeight: isMaximized ? '600px' : '400px',
            width: isMaximized ? '100%' : '350px',
            display: 'flex',
            flexDirection: 'column',
            position: isMaximized ? 'relative' : 'fixed',
            bottom: isMaximized ? 'auto' : 80,
            right: isMaximized ? 'auto' : 16,
            margin: isMaximized ? 'auto' : 0,
            borderRadius: isMaximized ? 1 : 2,
            boxShadow: isMaximized ? 3 : 4
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          backgroundColor: '#1976d2', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
              Asistente Virtual Sentry
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              aria-label="toggle maximize"
              onClick={toggleMaximize}
              size="small"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {isMaximized ? <MinimizeIcon sx={{ fontSize: 18 }} /> : <MaximizeIcon sx={{ fontSize: 18 }} />}
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              size="small"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          flex: 1, 
          p: 0, 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f5f5f5'
        }}>
          {/* Área de mensajes */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: '70%',
                    backgroundColor: message.sender === 'user' ? '#1976d2' : 'white',
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    boxShadow: 1
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.text}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      mt: 0.5, 
                      opacity: 0.7,
                      fontSize: '0.7rem'
                    }}
                  >
                    {formatTime(message.timestamp)}
                  </Typography>
                </Paper>
              </Box>
            ))}
            
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Paper sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'white' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Escribiendo...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Área de input */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'white', 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end'
          }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              variant="outlined"
              size="small"
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatBot; 